import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import axios from "axios";

const BASE_URL = "https://backdanlu.eu-4.evennode.com";

const fmtPesoInput = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parsePesoInput = (v) => {
  if (!v) return "";
  const limpio = String(v)
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^0-9.-]/g, "");
  const num = Number(limpio);
  return Number.isNaN(num) ? "" : num;
};

export default function ModalCobroPagoExtraEntidad({
  show,
  onClose,
  defaultTipo = "CLIENTE",
  defaultEntidadId = "",
  defaultFecha = "",
  onSaved,
}) {
  const [form, setForm] = useState({
    fecha: "",
    lado: "HABER", // DEBE suma saldo, HABER resta saldo
    monto: "",
    mediopago: "EFECTIVO",
    observaciones: "",
  });

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [montoInput, setMontoInput] = useState("");

  useEffect(() => {
    if (!show) return;

    setError("");
    setForm({
      fecha: defaultFecha || new Date().toISOString().slice(0, 10),
      lado: "HABER",
      monto: "",
      mediopago: "EFECTIVO",
      observaciones: "",
    });
    setMontoInput("");
  }, [show, defaultFecha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const cerrar = () => {
    if (guardando) return;
    setError("");
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!defaultEntidadId) {
      setError("No hay entidad seleccionada.");
      return;
    }

    if (!form.fecha) {
      setError("Completá la fecha.");
      return;
    }

    const montoNum = Number(form.monto || 0);
    if (montoNum <= 0) {
      setError("El monto debe ser mayor a 0.");
      return;
    }

    if (!String(form.observaciones || "").trim()) {
      setError("Ingresá una observación.");
      return;
    }

    const token = localStorage.getItem("token") || "";

    const payload = {
      tipo: defaultTipo,
      entidadId: defaultEntidadId,
      fecha: form.fecha,
      lado: form.lado,
      monto: montoNum,
      mediopago: form.mediopago,
      observaciones: form.observaciones.trim(),
    };

    setGuardando(true);

    try {
      const url = `${BASE_URL}/movimientoextraentidad`;

      const resp = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          token,
        },
      });

      if (!resp?.data?.ok) {
        setError(resp?.data?.error || "No se pudo guardar el movimiento.");
        return;
      }

      onSaved?.(resp.data);
    } catch (err) {
      setError(err?.response?.data?.error || "Error al guardar el movimiento.");
    } finally {
      setGuardando(false);
    }
  };

  const labelEntidad = defaultTipo === "CLIENTE" ? "Cliente" : "Transporte";
  const ayudaLado =
    form.lado === "DEBE"
      ? "Va a sumar al saldo de la cuenta corriente."
      : "Va a restar del saldo de la cuenta corriente.";

  return (
    <Modal show={show} onHide={cerrar} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Cobro / Pago extraordinario</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Row className="g-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Tipo de entidad</Form.Label>
                <Form.Control value={labelEntidad} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha"
                  value={form.fecha}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Impacto</Form.Label>
                <Form.Select
                  name="lado"
                  value={form.lado}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="HABER">HABER</option>
                  <option value="DEBE">DEBE</option>
                </Form.Select>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  {ayudaLado}
                </div>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="text"
                  name="monto"
                  value={montoInput}
                  onChange={(e) => {
                    setMontoInput(e.target.value);
                  }}
                  onFocus={() => {
                    if (form.monto !== "" && form.monto !== null) {
                      setMontoInput(String(form.monto).replace(".", ","));
                    }
                  }}
                  onBlur={() => {
                    const parsed = parsePesoInput(montoInput);
                    setForm((prev) => ({
                      ...prev,
                      monto: parsed,
                    }));
                    setMontoInput(parsed === "" ? "" : `$ ${fmtPesoInput(parsed)}`);
                  }}
                  disabled={guardando}
                  placeholder="$ 0,00"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Medio de pago</Form.Label>
                <Form.Select
                  name="mediopago"
                  value={form.mediopago}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="EFECTIVO">EFECTIVO</option>
                  <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="OTRO">OTRO</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  type="text"
                  name="observaciones"
                  value={form.observaciones}
                  onChange={handleChange}
                  disabled={guardando}
                  placeholder="Ej: Pago deuda vieja / Cobro extraordinario"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>

          <Button variant="success" type="submit" disabled={guardando}>
            {guardando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}